import { apiClient, isFeatureEnabled } from "./authService";
import { unwrapItem, unwrapList } from "../utils/apiResponse";

export type CompensationType =
  | "MONTHLY"
  | "HOURLY"
  | "PER_CLASS"
  | "CUSTOM_PER_CLASS";

export interface CoachProfile {
  id?: string;
  compensation_type?: CompensationType;
  compensation_rate?: number | string;
  active?: boolean;
  bio?: string | null;
}

export interface CoachResource {
  id?: string;
  name?: string;
}

export interface Coach {
  id: string;
  full_name?: string;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  bio?: string | null;
  active?: boolean;
  coach_profile?: CoachProfile | null;
  coach_resource?: CoachResource | null;
  /** Outstanding balance owed (lifetime earned − lifetime paid). */
  total_payout?: number;
  [key: string]: any;
}

export interface CreateCoachPayload {
  full_name: string;
  country: string;
  phone: string;
  email?: string;
  bio?: string;
  compensation_type: CompensationType;
  /** Required for every type except CUSTOM_PER_CLASS, which omits it entirely. */
  compensation_rate?: number;
  active?: boolean;
}

export interface UpdateCoachPayload {
  full_name?: string;
  bio?: string;
  compensation_type?: CompensationType;
  compensation_rate?: number;
  active?: boolean;
}

export interface CoachPayoutSession {
  appointment_id: string;
  class_name?: string;
  start_at?: string;
  end_at?: string;
  duration_hours?: number;
  /** CUSTOM_PER_CLASS only — the manually entered payout for this session. */
  amount?: number | null;
  amount_entered?: boolean;
  entered_by?: string | null;
  entered_at?: string | null;
}

export interface CoachPayout {
  coach_id: string;
  compensation_type?: CompensationType;
  compensation_rate?: number;
  period?: { from?: string; to?: string };
  session_count?: number;
  total_payout?: number;
  sessions?: CoachPayoutSession[];
  /** CUSTOM_PER_CLASS only — how many DONE sessions in range still need an amount. */
  pending_amount_count?: number;
  /**
   * Lifetime amount already paid — only present on the no-params (outstanding
   * balance) call, not on an explicit from/to period report.
   */
  total_paid?: number;
}

export interface PayCoachPayload {
  /** The billing account to pay from — same picker as regular expense creation. */
  accountId: number;
  currency: string;
  /** Which business this payout is booked under — same picker as regular expense creation. */
  businessId?: string;
  /** Which unit — omitted when the payment isn't scoped to a specific unit/branch. */
  unitId?: string;
  /** Omit to pay the full outstanding balance; capped at outstanding (server rejects overpayment). */
  amount?: number;
  /** Prefill from the payout breakdown, editable; server auto-generates a default if omitted. */
  description?: string;
  notes?: string;
}

export interface CoachPayment {
  id: string;
  amount: number;
  description?: string;
  expense_id?: string;
  paid_by?: string;
  created_at?: string;
  [key: string]: any;
}

export interface PayCoachResponse {
  payment: CoachPayment;
  expense: Record<string, any>;
  /** Outstanding balance after this payment. */
  remaining_outstanding: number;
}

export interface CoachPayoutSessionsList {
  coach_id: string;
  period?: { from?: string; to?: string };
  session_count: number;
  entered_count: number;
  pending_count: number;
  total_entered_amount: number;
  sessions: CoachPayoutSession[];
}

export const createCoach = async (payload: CreateCoachPayload): Promise<Coach> => {
  const response = await apiClient.post("/coaches", payload);
  return unwrapItem(response.data, "coach") as Coach;
};

export const getAllCoaches = async (): Promise<Coach[]> => {
  // Coaches live behind the CLASSES switch — skip the guaranteed 403.
  if (!isFeatureEnabled("CLASSES")) return [];
  const response = await apiClient.get("/coaches");
  return unwrapList(response.data, "coaches") as Coach[];
};

export const getCoachById = async (id: string): Promise<Coach> => {
  const response = await apiClient.get(`/coaches/${id}`);
  return unwrapItem(response.data, "coach") as Coach;
};

export const updateCoach = async (
  id: string,
  payload: UpdateCoachPayload
): Promise<Coach> => {
  const response = await apiClient.patch(`/coaches/${id}`, payload);
  return unwrapItem(response.data, "coach") as Coach;
};

/**
 * Called with no `from`/`to` (the default — what the money icon uses):
 * returns the coach's outstanding balance (lifetime earned − lifetime paid,
 * same number as `total_payout` in the coaches list) plus `total_paid` and
 * the full session breakdown since the coach's join date. Use this to
 * populate the payout/Pay dialog.
 *
 * Called with explicit `from`/`to` (ISO dates): unchanged — a plain period
 * report with no payment subtraction, for "view a specific month" UIs.
 *
 * Only counts sessions marked DONE. For CUSTOM_PER_CLASS coaches,
 * total_payout only sums amounts actually entered so far, and
 * `pending_amount_count` says how many still need one.
 */
export const getCoachPayout = async (
  id: string,
  range?: { from?: string; to?: string }
): Promise<CoachPayout> => {
  const response = await apiClient.get(`/coaches/${id}/payout`, {
    params: {
      from: range?.from || undefined,
      to: range?.to || undefined,
    },
  });
  return unwrapItem(response.data, "payout") as CoachPayout;
};

/**
 * Pays a coach against their outstanding balance — creates a wage expense
 * (metadata.type: 'WAGE') from the given account and reduces `total_payout`
 * going forward. Supports partial payments: omit `amount` to pay the full
 * outstanding balance, or pass a lesser amount to leave the remainder owed.
 */
export const payCoach = async (
  id: string,
  payload: PayCoachPayload
): Promise<PayCoachResponse> => {
  const response = await apiClient.post(`/coaches/${id}/pay`, payload);
  return unwrapItem(response.data, "result") as PayCoachResponse;
};

/**
 * CUSTOM_PER_CLASS coaches only — 400s for any other compensation type.
 * Lists every DONE session in range needing (or already given) a manually
 * entered payout amount. `from`/`to` default to start-of-month → now.
 */
export const getCoachPayoutSessions = async (
  id: string,
  range?: { from?: string; to?: string }
): Promise<CoachPayoutSessionsList> => {
  const response = await apiClient.get(`/coaches/${id}/payout-sessions`, {
    params: {
      from: range?.from || undefined,
      to: range?.to || undefined,
    },
  });
  return unwrapItem(response.data, "result") as CoachPayoutSessionsList;
};

/**
 * Sets (or edits — safe to re-send for the same session, updates in place)
 * the payout amount for one CUSTOM_PER_CLASS session.
 */
export const setCoachPayoutSessionAmount = async (
  coachId: string,
  appointmentId: string,
  amount: number
): Promise<CoachPayoutSession> => {
  const response = await apiClient.patch(
    `/coaches/${coachId}/payout-sessions/${appointmentId}`,
    { amount }
  );
  return unwrapItem(response.data, "session") as CoachPayoutSession;
};
